import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, Plus, X } from 'lucide-react';
import { api } from '../api/client';
import {
  PageHeader, Table, Th, Td, TrEmpty, Badge, Btn, LoadingState, ErrorState,
} from '../components/ui';

interface Entity {
  id: string;
  name: string;
  suspendedAt: string | null;
  deletedAt: string | null;
}

function CreateEntityModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () => api.post<Entity>('/entities', { name: name.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'entities'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-bg-surface border border-white/[0.06] rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">Create Entity</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1.5">Organization Name</label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Acme Corp"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50"
              onKeyDown={e => { if (e.key === 'Enter' && name.trim()) create.mutate(); }}
            />
          </div>

          {create.isError && (
            <p className="text-sm text-red-400">{(create.error as Error).message}</p>
          )}
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-xl text-sm text-gray-400 bg-white/5 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!name.trim() || create.isPending}
            className="flex-1 py-2 rounded-xl text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {create.isPending ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function Entities() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error } = useQuery<{ entities: Entity[] }>({
    queryKey: ['admin', 'entities'],
    queryFn: () => api.get('/entities'),
  });

  const suspend = useMutation({
    mutationFn: (id: string) => api.post(`/entities/${id}/suspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'entities'] }),
  });

  const unsuspend = useMutation({
    mutationFn: (id: string) => api.post(`/entities/${id}/unsuspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'entities'] }),
  });

  const entities = data?.entities ?? [];
  const pending = suspend.isPending || unsuspend.isPending;

  return (
    <div>
      <PageHeader
        title="Entities"
        subtitle={data ? `${entities.length} total` : undefined}
        action={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Entity
          </button>
        }
      />

      {isLoading && <LoadingState />}
      {error && <ErrorState message="Error loading entities" />}

      {!isLoading && !error && (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>ID</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {entities.length === 0 ? (
              <TrEmpty cols={4} message="No entities found" />
            ) : (
              entities.map(e => {
                const isSuspended = !!e.suspendedAt;
                const isDeleted = !!e.deletedAt;
                return (
                  <tr key={e.id} className="hover:bg-white/[0.02] transition-colors">
                    <Td>
                      <Link
                        to={`/entities/${e.id}`}
                        className="flex items-center gap-1.5 text-primary hover:text-primary/80 font-medium transition-colors"
                      >
                        {e.name}
                        <ExternalLink className="h-3 w-3 opacity-60" />
                      </Link>
                    </Td>
                    <Td className="font-mono text-xs text-gray-400">{e.id}</Td>
                    <Td>
                      {isDeleted ? (
                        <Badge variant="danger">Deleted</Badge>
                      ) : isSuspended ? (
                        <Badge variant="warning">Suspended</Badge>
                      ) : (
                        <Badge variant="success">Active</Badge>
                      )}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        {!isSuspended && !isDeleted && (
                          <Btn variant="danger" disabled={pending} onClick={() => suspend.mutate(e.id)}>
                            Suspend
                          </Btn>
                        )}
                        {isSuspended && !isDeleted && (
                          <Btn variant="ghost" disabled={pending} onClick={() => unsuspend.mutate(e.id)}>
                            Unsuspend
                          </Btn>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      {showCreate && <CreateEntityModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
